import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  UserReviewChanges,
  approveUserReview,
  deleteUserReview,
  fetchUserReviewPage,
  updateUserReview,
} from 'api';
import { UserReview, UserReviewStatus } from 'types';

export type UserReviewFilters = {
  status?: UserReviewStatus;
  bookId?: number;
  author?: string;
};

export const useUserReviews = (
  page: number,
  filters: UserReviewFilters,
  enabled = true,
) =>
  useQuery({
    queryKey: ['user-reviews', page, filters],
    queryFn: () => fetchUserReviewPage(page, { ...filters, perPage: 10 }),
    placeholderData: keepPreviousData,
    enabled,
  });

export const useModerateUserReview = () => {
  const queryClient = useQueryClient();
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['user-reviews'] }),
      queryClient.invalidateQueries({ queryKey: ['reviews'] }),
    ]);

  const approve = useMutation({
    mutationFn: (review: UserReview) => approveUserReview(review),
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: (id: number) => deleteUserReview(id),
    onSuccess: refresh,
  });

  const update = useMutation({
    mutationFn: ({ id, changes }: { id: number; changes: UserReviewChanges }) =>
      updateUserReview(id, changes),
    onSuccess: refresh,
  });

  return { approve, remove, update };
};
